/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { Select } from '../Select/Select'
import { usersApiService } from '../../services/api/users'

// GroupType constant from legacy code
const GroupType = {
  CLOUDADMIN_ACL: 'CLOUDADMIN_ACL'
}

export interface SelectUserGroupOption {
  value: string
  label: string
  id: number
  systemHelperType: string
  isDisabled?: boolean
}

export interface SelectUserGroupProps {
  value?: string | string[]
  name: string
  onChange: (value: string | string[] | null) => void
  placeholder?: string
  isMulti?: boolean
  onlyUsers?: boolean
  onlyGroups?: boolean
  isDisabled?: boolean
}

/**
 * SelectUserGroup - Component for selecting users and/or groups
 *
 * Migrated from legacy lego/component/SelectUserGroup.jsx
 * Uses modern React patterns with hooks and TypeScript
 */
export function SelectUserGroup(props: SelectUserGroupProps): React.ReactElement {
  const [value, setValue] = useState<string | string[]>([])
  const [usersGroups, setUsersGroups] = useState<SelectUserGroupOption[]>([])

  const updateValue = (selection: SelectUserGroupOption | SelectUserGroupOption[] | null) => {
    const newValue = _.isArray(selection) ? _.map(selection, 'value') : (selection?.value ?? null)

    setValue(newValue ?? [])
    props.onChange(newValue)
  }

  // Sync internal value with props.value when options are loaded
  useEffect(() => {
    if (!usersGroups.length) return

    const hasChanges = _.isArray(props.value)
      ? _.difference(props.value, _.isArray(value) ? value : []).length > 0
      : props.value !== value

    if (hasChanges || !props.value) {
      if (_.isArray(props.value)) {
        const initialValues = props.value
          .map((userGroupItem: any) => {
            // Support both object and string/ID
            let key: string | undefined = undefined
            if (userGroupItem && typeof userGroupItem === 'object') {
              key = userGroupItem._uid || (userGroupItem.id ? String(userGroupItem.id) : undefined)
            } else {
              key = String(userGroupItem)
            }
            const found = key ? _.find(usersGroups, { value: key }) : undefined
            return found
          })
          .filter(Boolean) as SelectUserGroupOption[]

        if (initialValues.length) {
          updateValue(initialValues)
        }
      } else if (!props.value) {
        setValue([])
      } else {
        const foundUserGroup = _.find(usersGroups, { value: props.value })
        if (foundUserGroup) {
          updateValue(foundUserGroup)
        }
      }
    }
  }, [props.value, usersGroups])

  // Load users and groups on mount
  useEffect(() => {
    usersApiService.getAssembledUsersGroups().then((data: any) => {
      const sourceData = props.onlyUsers ? data.users : props.onlyGroups ? data.groups : data.merged

      const usersGroupsFiltered = _.chain(sourceData)
        .map((user) => ({
          value: user._uid,
          label: user.chName,
          id: user.id,
          systemHelperType: user.systemHelperType,
          ...(user?.type === GroupType.CLOUDADMIN_ACL ? { isDisabled: true } : {})
        }))
        .value()

      setUsersGroups(usersGroupsFiltered)
    })
  }, [props.onlyUsers, props.onlyGroups])

  return (
    <Select
      placeholder={props.placeholder}
      name={props.name}
      value={value}
      options={usersGroups}
      onChange={(option: any) => updateValue(option)}
      showNewOptionAtTop={true}
      searchable
      multi={props.isMulti ? true : false}
      id="2a8b6ccd-ae67-4706-a399-dfef4d0ec5db"
      isDisabled={props.isDisabled}
    />
  )
}
